import { createClient } from './http';
import type { LayerGroup } from '../admin/types';

const MAPS_API_BASE = import.meta.env.VITE_MAPS_API_BASE_URL ?? 'https://x-gis.xenus.dev/api/maps';

const request = createClient(MAPS_API_BASE);

type LayerGroupDto = {
  id: string;
  map_id: string;
  name: string;
  color: string;
  parent_id: string | null;
  sort_order: number;
  default_expanded: boolean;
  default_visible: boolean;
  visibility_mode: 'independent' | 'exclusive';
  min_zoom: number | null;
  max_zoom: number | null;
};

function fromDto(dto: LayerGroupDto): LayerGroup {
  return {
    id: dto.id,
    name: dto.name,
    color: dto.color,
    parentId: dto.parent_id,
    sortOrder: dto.sort_order,
    defaultExpanded: dto.default_expanded,
    defaultVisible: dto.default_visible,
    visibilityMode: dto.visibility_mode,
    minZoom: dto.min_zoom,
    maxZoom: dto.max_zoom,
  };
}

function toPayload(group: Omit<LayerGroup, 'id' | 'sortOrder'>) {
  return {
    name: group.name,
    color: group.color,
    parent_id: group.parentId,
    default_expanded: group.defaultExpanded,
    default_visible: group.defaultVisible,
    visibility_mode: group.visibilityMode,
    min_zoom: group.minZoom,
    max_zoom: group.maxZoom,
  };
}

export const layerGroupsApi = {
  listLayerGroups: async (mapId: string): Promise<LayerGroup[]> => {
    const rows = await request<LayerGroupDto[]>(`/layer-groups?map_id=${mapId}`, {}, true);
    return rows.map(fromDto);
  },

  createLayerGroup: async (mapId: string, group: Omit<LayerGroup, 'id' | 'sortOrder'>): Promise<LayerGroup> => {
    const dto = await request<LayerGroupDto>(
      '/layer-groups',
      { method: 'POST', body: JSON.stringify({ map_id: mapId, ...toPayload(group) }) },
      true,
    );
    return fromDto(dto);
  },

  updateLayerGroup: async (id: string, group: Omit<LayerGroup, 'id' | 'sortOrder'>): Promise<LayerGroup> => {
    const dto = await request<LayerGroupDto>(
      `/layer-groups/${id}`,
      { method: 'PATCH', body: JSON.stringify(toPayload(group)) },
      true,
    );
    return fromDto(dto);
  },

  deleteLayerGroup: async (id: string): Promise<void> => {
    await request<void>(`/layer-groups/${id}`, { method: 'DELETE' }, true);
  },

  moveLayerGroup: async (id: string, direction: 'up' | 'down'): Promise<LayerGroup[]> => {
    const rows = await request<LayerGroupDto[]>(`/layer-groups/${id}/move?direction=${direction}`, { method: 'POST' }, true);
    return rows.map(fromDto);
  },
};
